import { styled } from 'styled-components'
import { ReactNode, useEffect, useState } from 'react'

import PanelBackgroundImage from '@/assets/Paytrie_Patterns_arrow_snow.png'
import { InteracLabel, PoweredByLabel } from '@/components/widget-components'

const WidgetBackground = styled.div`
  position: relative;
  &:after {
    background-image: url(${PanelBackgroundImage});
    background-size: cover;
    background-repeat: no-repeat;
    background-position: center;
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100px;
    width: 100%;
    z-index: -1;
  }
`

interface FooterProps {
  showInteracLabel: boolean
  showPoweredByLabel: boolean
}

const Footer = ({
  showInteracLabel = false,
  showPoweredByLabel,
}: FooterProps) => {
  return (
    <div
      data-testid="container-widget-footer"
      className="absolute bottom-0 left-0 right-0 flex flex-col items-center justify-center pb-4"
    >
      {showInteracLabel && (
        <div data-testid="container-interac-label" className="mb-2">
          <InteracLabel />
        </div>
      )}
      {showPoweredByLabel && (
        <div data-testid="container-powered-label">
          <PoweredByLabel />
        </div>
      )}
    </div>
  )
}

interface WidgetLayoutProps {
  children: ReactNode
  showInteracLabel?: boolean
  showPoweredByLabel?: boolean
  heightClassName?: string
}

export const WidgetLayout = ({
  children,
  showInteracLabel = false,
  showPoweredByLabel = true,
  heightClassName = 'min-h-[max(700px,100dvh)]',
}: WidgetLayoutProps) => {
  const getInitialMode = () => {
    const savedMode = localStorage.getItem('darkMode')
    return savedMode
      ? savedMode === 'true'
      : window.matchMedia('(prefers-color-scheme: dark)').matches
  }

  const [darkMode, setDarkMode] = useState(getInitialMode)

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }

    const handleMessageFromParent = (event: {
      data: { darkMode: boolean }
    }) => {
      if (event.data && typeof event.data.darkMode === 'boolean') {
        setDarkMode(event.data.darkMode)
        localStorage.setItem('darkMode', String(event.data.darkMode))
      }
    }

    window.addEventListener('message', handleMessageFromParent)
    return () => window.removeEventListener('message', handleMessageFromParent)
  }, [darkMode])

  useEffect(() => {
    const syncDarkModeAcrossTabs = (event: StorageEvent) => {
      if (event.key === 'darkMode') {
        setDarkMode(event.newValue === 'true')
      }
    }
    window.addEventListener('storage', syncDarkModeAcrossTabs)
    return () => window.removeEventListener('storage', syncDarkModeAcrossTabs)
  }, [])

  return (
    <WidgetBackground
      data-testid="container-widget-background"
      className="after:invert dark:after:invert-0"
    >
      <div
        data-testid="container-widget-content"
        className={`relative flex flex-col gap-3 px-6 pt-8 pb-10 md:gap-5 md:pt-10 ${heightClassName} overflow-hidden`}
      >
        {children}
        <Footer
          showInteracLabel={showInteracLabel}
          showPoweredByLabel={showPoweredByLabel}
        />
      </div>
    </WidgetBackground>
  )
}
